/**
 * サブスクリプションステータスEnum
 * Stripe API互換: Stripe APIは小文字のステータスを返すため、値は小文字を維持
 *
 * @see https://stripe.com/docs/api/subscriptions/object#subscription_object-status
 * @see backend/src/modules/payment/enums/subscription-status.enum.ts (Backend版)
 * @important 変更時はBackend版も同時に更新すること
 *
 * @description
 * - ACTIVE: 有効なサブスクリプション
 * - PAST_DUE: 支払い遅延（決済失敗、リトライ中。 SubscriptionGuard で即 block — Issue #703 strict policy、 grace period なし）
 * - CANCELED: キャンセル済み
 * - UNPAID: 未払い（支払い失敗後）
 * - INCOMPLETE: 初回支払い未完了
 * - INCOMPLETE_EXPIRED: 初回支払い期限切れ
 * - PAUSED: 一時停止中
 *
 * @note `trialing` は SalonHub では未提供（`docs/requirements/business-rules.md:32` 「無料トライアル: なし」）
 *       のため除外。 Backend 版と同期。
 */
export enum SubscriptionStatusEnum {
  ACTIVE = 'active',
  PAST_DUE = 'past_due',
  CANCELED = 'canceled',
  UNPAID = 'unpaid',
  INCOMPLETE = 'incomplete',
  INCOMPLETE_EXPIRED = 'incomplete_expired',
  PAUSED = 'paused',
}

/**
 * Stripe互換のサブスクリプションステータス型（小文字）
 */
export type SubscriptionStatus =
  | 'active'
  | 'past_due'
  | 'canceled'
  | 'unpaid'
  | 'incomplete'
  | 'incomplete_expired'
  | 'paused';

/**
 * サブスクリプションが有効かどうかを判定
 */
export function isSubscriptionActive(
  status: SubscriptionStatus | string
): boolean {
  return status === SubscriptionStatusEnum.ACTIVE;
}

/**
 * サブスクリプションが支払い問題を抱えているかを判定
 */
export function hasPaymentIssue(status: SubscriptionStatus | string): boolean {
  return (
    status === SubscriptionStatusEnum.PAST_DUE ||
    status === SubscriptionStatusEnum.UNPAID ||
    status === SubscriptionStatusEnum.INCOMPLETE
  );
}

/**
 * ステータスの日本語ラベルを取得
 */
export function getSubscriptionStatusLabel(
  status: SubscriptionStatus | string
): string {
  const labels: Record<string, string> = {
    active: '有効',
    past_due: '支払い遅延',
    canceled: 'キャンセル済み',
    unpaid: '未払い',
    incomplete: '支払い未完了',
    incomplete_expired: '支払い期限切れ',
    paused: '一時停止中',
  };
  return labels[status] || status;
}
